import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getAtsActiveVacancies } from '../services/api';

type AtsVacancyItem = {
  id: string;
  externalId: string;
  source: string;
  title: string;
  code?: string | null;
  status?: string | null;
  displayName: string;
};

type AtsVacancySelectProps = {
  source: string;
  value?: string;
  selectedTitle?: string;
  onChange: (vacancy: { atsVacancyId: string; atsVacancyTitle: string }) => void;
  onClear: () => void;
};

const PAGE_SIZE = 100;
const QUERY_DEBOUNCE_MS = 300;

const AtsVacancySelect: React.FC<AtsVacancySelectProps> = ({
  source,
  value = '',
  selectedTitle = '',
  onChange,
  onClear
}) => {
  const requestIdRef = useRef(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<AtsVacancyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedLabel = (() => {
    if (selectedTitle.trim()) return selectedTitle.trim();
    if (value.trim()) return `Вакансия ${value.trim()}`;
    return '';
  })();

  const loadVacancies = useCallback(async (searchQuery = '') => {
    if (!source || source === 'hh') return;

    const requestId = ++requestIdRef.current;
    setError('');
    setLoading(true);

    try {
      const data = await getAtsActiveVacancies({
        source,
        query: searchQuery.trim() || undefined,
        page: 1,
        pageSize: PAGE_SIZE,
        activeOnly: true
      });

      if (requestId !== requestIdRef.current) return;

      const nextItems = Array.isArray(data?.items) ? data.items : [];
      const nextTotal = typeof data?.total === 'number' ? data.total : nextItems.length;

      setItems(nextItems);
      setTotal(nextTotal);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.response?.data?.error || 'Не удалось загрузить вакансии ATS');
      setItems([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [source]);

  useEffect(() => {
    if (!isModalOpen) return;
    // Debounce: при наборе query не отправляем запрос на каждый keystroke.
    const handle = setTimeout(() => {
      void loadVacancies(query);
    }, QUERY_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [isModalOpen, loadVacancies, query]);

  useEffect(() => {
    requestIdRef.current += 1;
    setIsModalOpen(false);
    setQuery('');
    setItems([]);
    setTotal(0);
    setError('');
  }, [source]);

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:outline-none bg-white hover:bg-[#f6f8fa] transition-colors flex items-center justify-between"
      >
        <span className={selectedLabel ? 'text-[#0a0d14]' : 'text-gray-400'}>
          {selectedLabel || 'Выбрать вакансию из ATS'}
        </span>
        <div className="flex items-center gap-2">
          {value ? (
            <span
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
              className="p-0.5 hover:bg-[#F6F8FA] rounded"
            >
              <XMarkIcon className="w-4 h-4 text-[#525866]" />
            </span>
          ) : null}
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        </div>
      </button>

      {isModalOpen ? (
        <>
          <div className="fixed inset-0 bg-black/30 z-[10001]" onClick={() => setIsModalOpen(false)} />
          <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4">
            <div className="bg-white rounded-[24px] w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl">
              <div className="flex items-center justify-between p-6 border-b border-[#E2E4E9]">
                <h3 className="text-xl font-medium text-[#0A0D14]">Выберите вакансию из ATS</h3>
                <button type="button" onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-[#F6F8FA] rounded-lg">
                  <XMarkIcon className="w-6 h-6 text-[#525866]" />
                </button>
              </div>
              <div className="p-4 border-b border-[#E2E4E9]">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-4 h-4 text-[#868c98] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Поиск вакансии по названию"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="w-full px-3 py-2.5 pl-9 rounded-[10px] border border-[#E2E4E9] bg-white text-sm"
                    autoFocus
                  />
                </div>
                <div className="mt-3 text-xs text-[#868c98]">
                  {total > items.length
                    ? `Показано ${items.length} из ${total} — уточните поиск`
                    : total > 0
                      ? `Активных вакансий: ${total}`
                      : 'Только активные вакансии'}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                  <div className="py-10 text-center text-sm text-[#868c98]">Загрузка вакансий...</div>
                ) : error ? (
                  <div className="py-10 text-center text-sm text-[#d14343]">{error}</div>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center text-sm text-[#868c98]">
                    {query.trim() ? 'Ничего не найдено' : 'Активные вакансии не найдены'}
                  </div>
                ) : (
                  items.map((item) => {
                    const isSelected = value === item.externalId || value === item.id;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          onChange({
                            atsVacancyId: item.externalId || item.id,
                            atsVacancyTitle: item.displayName || item.title
                          });
                          setIsModalOpen(false);
                        }}
                        className={`w-full px-3 py-3 rounded-[10px] text-left mb-2 transition-colors ${
                          isSelected ? 'bg-[#FEF3F2] text-[#E16349]' : 'hover:bg-[#F6F8FA] text-[#525866]'
                        }`}
                      >
                        <div className="text-sm leading-5">{item.displayName || item.title}</div>
                        <div className="mt-1 text-xs text-[#868c98]">
                          {item.status ? `${item.status} · ${item.externalId}` : item.externalId}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AtsVacancySelect;
